import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X, Copy, Check, Code2 } from 'lucide-react'
import { adminApi } from '@/api/admin'
import { Button } from '@/components/ui/button'

interface Props {
  onClose: () => void
}

const DEFAULT_COLOUR = '#6366f1'
const HEIGHT_OPTIONS = [300, 400, 500, 600, 800]

export default function EmbedSnippetModal({ onClose }: Props) {
  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })

  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })

  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([])
  const [primaryColour, setPrimaryColour] = useState(DEFAULT_COLOUR)
  const [height, setHeight] = useState(500)
  const [limit, setLimit] = useState(10)
  const [copied, setCopied] = useState(false)

  const slug = profile?.slug ?? ''

  const embedUrl = useMemo(() => {
    const base = `${window.location.origin}/embed/${slug}`
    const p = new URLSearchParams()
    if (selectedCourseIds.length > 0) p.set('courseIds', selectedCourseIds.join(','))
    if (primaryColour !== DEFAULT_COLOUR) p.set('primaryColour', primaryColour)
    if (limit !== 10) p.set('limit', String(limit))
    const qs = p.toString()
    return qs ? `${base}?${qs}` : base
  }, [slug, selectedCourseIds, primaryColour, limit])

  const snippet = `<iframe\n  src="${embedUrl}"\n  width="100%"\n  height="${height}"\n  style="border:none;border-radius:8px"\n  loading="lazy"\n  title="Training sessions"\n></iframe>`

  const handleCopy = () => {
    navigator.clipboard.writeText(snippet)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const toggleCourse = (id: string) => {
    setSelectedCourseIds(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <Code2 size={18} className="text-indigo-500" />
            <h2 className="font-semibold text-slate-900">Embed on your website</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">
          {/* Config panel */}
          <div className="lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-100 p-5 space-y-5 overflow-y-auto">
            {/* Course filter */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Courses</p>
              <label className="flex items-center gap-2 text-sm text-slate-700 mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedCourseIds.length === 0}
                  onChange={() => setSelectedCourseIds([])}
                  className="rounded"
                  readOnly
                />
                All courses
              </label>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {courses.map(c => (
                  <label key={c.id} className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCourseIds.includes(c.id)}
                      onChange={() => toggleCourse(c.id)}
                      className="rounded mt-0.5 shrink-0"
                    />
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Colour */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Button colour</p>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={primaryColour}
                  onChange={e => setPrimaryColour(e.target.value)}
                  className="h-8 w-12 rounded cursor-pointer border border-slate-200 p-0.5"
                />
                <span className="text-sm text-slate-500 font-mono">{primaryColour}</span>
                {primaryColour !== DEFAULT_COLOUR && (
                  <button
                    type="button"
                    onClick={() => setPrimaryColour(DEFAULT_COLOUR)}
                    className="text-xs text-slate-400 hover:text-slate-600"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Height */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Height (px)</p>
              <div className="flex flex-wrap gap-1.5">
                {HEIGHT_OPTIONS.map(h => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setHeight(h)}
                    className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                      height === h
                        ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-semibold'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>

            {/* Limit */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Max sessions</p>
              <select
                value={limit}
                onChange={e => setLimit(parseInt(e.target.value))}
                className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                {[3, 5, 10, 20].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Preview + snippet */}
          <div className="flex-1 flex flex-col overflow-hidden p-5 space-y-4">
            {/* Live preview */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Preview</p>
              {slug ? (
                <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                  <iframe
                    key={embedUrl}
                    src={embedUrl}
                    width="100%"
                    height={Math.min(height, 300)}
                    style={{ border: 'none', display: 'block' }}
                    title="Embed preview"
                  />
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center h-24">
                  <p className="text-sm text-slate-400">No tenant slug configured</p>
                </div>
              )}
            </div>

            {/* Snippet */}
            <div className="flex-1 flex flex-col min-h-0">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Embed code</p>
              <div className="relative flex-1 min-h-0">
                <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs font-mono overflow-auto h-full leading-relaxed whitespace-pre">
                  {snippet}
                </pre>
              </div>
            </div>

            <Button onClick={handleCopy} className="w-full shrink-0">
              {copied ? <><Check size={15} className="mr-1.5" /> Copied!</> : <><Copy size={15} className="mr-1.5" /> Copy snippet</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
